import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker, InputGroup} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { SubscriptionDurationList, SubscriptionPlan, SubscriptionTierEnum, SubscriptionTierList, UpsertSubscriptionPlan } from '@/models/subscription-plan.model';
import { upsertSubscriptionPlan } from '@/services/subscription-plan.service';
import { Icon } from '@rsuite/icons';
import { MdContentPaste } from 'react-icons/md';

const subscriptionPlanSchema = Schema.Model<UpsertSubscriptionPlan>({
  id: Schema.Types.NumberType(),
  appleProductId: Schema.Types.StringType(),
  googleBasePlanId: Schema.Types.StringType(),
  tier: Schema.Types.NumberType().isRequired('This field is required.'),
  duration: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertSubscriptionPlan = {
  id: undefined,
  appleProductId: undefined,
  googleBasePlanId: undefined,
  tier: SubscriptionTierEnum.Premium,
  duration: undefined,
};

interface SubscriptionPlanFormProps{
  formObject?: SubscriptionPlan;
  pageState: PageState
};

const SubscriptionPlanForm: React.FC<SubscriptionPlanFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertSubscriptionPlan>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<SubscriptionPlan> = await upsertSubscriptionPlan(formValue);
      if (result.success) {
        nav('/subscription/plans');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='subscription-plan-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={subscriptionPlanSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Tier</Form.Label>
                <Form.Control name="tier" accepter={SelectPicker} block
                  data={SubscriptionTierList} 
                  searchable={false}
                  placeholder="Select a tier"
                /> 
              </Form.Group>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Duration</Form.Label>
                <Form.Control name="duration" accepter={SelectPicker} block
                  data={SubscriptionDurationList} 
                  searchable={false}
                  placeholder="Select a duration"
                /> 
              </Form.Group>
            </Col>
          </Row> 
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Apple Product Id</Form.Label>
                <InputGroup inside>
                  <Form.Control name="appleProductId" />
                    <InputGroup.Button onClick={() => navigator.clipboard.readText().then(res => 
                        setFormValue(pre => ({...pre, appleProductId: res ?? formValue.appleProductId})) 
                    )}>
                    <Icon as={MdContentPaste} />
                  </InputGroup.Button>
                </InputGroup>                     
              </Form.Group>                 
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Google Base Plan Id</Form.Label>
                <InputGroup inside>
                  <Form.Control name="googleBasePlanId" />
                  <InputGroup.Button onClick={() => navigator.clipboard.readText().then(res => 
                      setFormValue(pre => ({...pre, googleBasePlanId: res ?? formValue.googleBasePlanId})) 
                  )}>
                    <Icon as={MdContentPaste} />
                  </InputGroup.Button>
                </InputGroup>                     
              </Form.Group>                 
            </Col>
          </Row>
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default SubscriptionPlanForm;

